// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {
  createReference,
  type GroupCode,
  GroupCodeDescription,
  GroupDescription,
} from '@quromedical/fhir-common';
import type { GroupMember } from '@quromedical/models';
import { Row } from 'components/base';
import { DisplayView } from 'components/fhir';
import { ConfirmationButton } from 'components/input';
import type { ColumnType } from 'components/table';
import { Text } from 'design-system';
import { logger } from 'helpers';
import { useRevalidation } from 'hooks/useRevalidation';
import { GroupApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback } from 'react';
import type { CellProps } from 'react-table';
import { strings } from 'strings';

import type { TableGroup } from '../config';

const api = new GroupApi();

export const columnsLarge: ColumnType<TableGroup>[] = [
  {
    header: strings.LabelActive,
    accessor: 'active',
    Cell: ({ value }) => (
      <Text variant="body-strong">{value ? 'Active' : 'Inactive'}</Text>
    ),
  } as ColumnType<TableGroup, 'active'>,
  {
    header: strings.DeviceTableTypeSubheading,
    accessor: 'type',
    Cell: ({ value }) => (
      <Text>{GroupCodeDescription[value as GroupCode] || value}</Text>
    ),
  } as ColumnType<TableGroup, 'type'>,
  {
    header: strings.DeviceTableNameSubheading,
    accessor: 'name',
  } as ColumnType<TableGroup, 'name'>,
  {
    header: strings.LabelPermissions,
    Cell: ({ value = [] }) =>
      value.map((permission, index) => (
        <Row key={index}>
          <Text>{GroupDescription[permission] || ''}</Text>
        </Row>
      )),
    accessor: 'permissions',
  } as ColumnType<TableGroup, 'permissions'>,
  {
    header: strings.LabelOrganization,
    Cell: ({ value }) => <DisplayView reference={value} />,
    accessor: 'organization',
  } as ColumnType<TableGroup, 'organization'>,
];

const createActionCell = (groupId: string) => {
  const AddCell: React.FC<CellProps<GroupMember>> = ({ row }) => {
    const memberId = row.original.id;
    const memberDisplay = row.original.display;

    // list data is still using the old FHIR endpoint
    const revalidate = useRevalidation(groupId);

    const onSubmit = useCallback(async () => {
      if (memberId) {
        await api.updateGroupMembers({
          id: groupId,
          active: true,
          members: [
            {
              id: memberId,
              display: memberDisplay,
            },
          ],
        });
      }
      await revalidate().catch(logger.error);
    }, [memberDisplay, memberId, revalidate]);

    return (
      <ConfirmationButton
        status="status-warning"
        title="Add Member"
        message="Are you sure you want to re-add this member to the group?"
        onSubmit={onSubmit}
        buttonText="Re-add"
      />
    );
  };

  const RemoveCell: React.FC<CellProps<GroupMember>> = ({ row }) => {
    const memberId = row.original.id;
    const memberDisplay = row.original.display;

    const revalidate = useRevalidation(groupId);

    const onSubmit = useCallback(async () => {
      if (memberId) {
        await api.updateGroupMembers({
          id: groupId,
          active: false,
          members: [
            {
              id: memberId,
              display: memberDisplay,
            },
          ],
        });
      }
      await revalidate().catch(logger.error);
    }, [memberDisplay, memberId, revalidate]);

    return (
      <ConfirmationButton
        status="status-critical"
        title="Remove Member"
        message="Are you sure you want to remove this member from the group?"
        onSubmit={onSubmit}
        buttonText="Remove"
      />
    );
  };

  const ActionCell: ColumnType<GroupMember, 'id'>['Cell'] = (props) => {
    const member = props.row.original;

    if (!member.active) {
      return <AddCell {...props} />;
    }

    return <RemoveCell {...props} />;
  };

  return ActionCell;
};

type MemberTable = GroupMember & { action: undefined };

export const memberColumns = (groupId: string): ColumnType<MemberTable>[] => [
  {
    accessor: 'id',
    header: strings.LabelEntity,
    Cell: ({ value, row }) => (
      <DisplayView
        reference={createReference('Practitioner', value, row.original.display)}
      />
    ),
  } as ColumnType<MemberTable, 'id'>,
  {
    accessor: 'active',
    header: strings.FormLabelActive,
    Cell: ({ value }) => <Text>{value ? 'Active' : 'Inactive'}</Text>,
  } as ColumnType<MemberTable, 'active'>,
  {
    accessor: 'action',
    header: strings.FormLabelActive,
    Cell: createActionCell(groupId),
  } as ColumnType<MemberTable, 'action'>,
];
